import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ALLOWED_ATTACHMENT_MIME_TYPES } from '@this+that/models';
import { sanitizeFileName } from '@this+that/utils';
import log from 'loglevel';

import { useGenerateCommentPresignedUrl } from '../../services/inboxEntryComments.service';
import { MAX_FILE_SIZE_BYTES } from '../../utils/constants';
import {
  uploadToPresignedPost,
  validateUploadFile,
} from '../../utils/fileHandlers/presignedUpload';
import type { UploadedAttachment } from '../Attachments/useAttachmentUploader';
import { useCustomToast } from '../useCustomToast';

export const useUploadCommentAttachments = (inboxEntryId: string) => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();
  const { generateCommentPresignedUrl } = useGenerateCommentPresignedUrl();

  const uploadFile = useCallback(
    async (
      file: File,
      signal?: AbortSignal,
    ): Promise<UploadedAttachment | null> => {
      if (
        !validateUploadFile(
          file,
          ALLOWED_ATTACHMENT_MIME_TYPES,
          MAX_FILE_SIZE_BYTES,
          t,
          errorMessage,
        )
      ) {
        return null;
      }

      let presigned;
      try {
        const result = await generateCommentPresignedUrl(
          {
            inboxEntryId,
            fileName: sanitizeFileName(file.name),
            fileType: file.type,
          },
          signal,
        );

        if (result.errors?.length) {
          if (signal?.aborted) {
            return null;
          }
          errorMessage(result.errors[0].message);
          return null;
        }

        presigned = result.data;
      } catch {
        if (signal?.aborted) {
          return null;
        }
        errorMessage(
          t('attachments.errors.presignedUrlFailed', { fileName: file.name }),
        );
        return null;
      }

      if (!presigned) {
        errorMessage(
          t('attachments.errors.presignedUrlFailed', { fileName: file.name }),
        );
        return null;
      }

      try {
        await uploadToPresignedPost(presigned, file, signal);
      } catch {
        if (signal?.aborted) {
          return null;
        }
        errorMessage(
          t('attachments.errors.uploadFailed', { fileName: file.name }),
        );
        return null;
      }
      return { attachmentId: presigned.attachmentId };
    },
    [inboxEntryId, generateCommentPresignedUrl, t, errorMessage],
  );

  const uploadAttachments = useCallback(
    async (files: File[]): Promise<string[]> => {
      const validFiles = files.filter((file) =>
        validateUploadFile(
          file,
          ALLOWED_ATTACHMENT_MIME_TYPES,
          MAX_FILE_SIZE_BYTES,
          t,
          errorMessage,
        ),
      );

      if (validFiles.length === 0) {
        return [];
      }

      const results = await Promise.allSettled(
        validFiles.map((file) => uploadFile(file)),
      );
      const attachmentIds: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          attachmentIds.push(result.value.attachmentId);
        } else if (result.status === 'rejected') {
          log.error(
            `Failed to upload attachment ${validFiles[index].name}:`,
            result.reason,
          );
          errorMessage(
            t('attachments.errors.failedToProcessFile', {
              fileName: validFiles[index].name,
            }),
          );
        }
      });

      return attachmentIds;
    },
    [uploadFile, errorMessage, t],
  );

  return { uploadFile, uploadAttachments };
};
